import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Button, ExclusiveChoiceDropdown, Loader, Text } from "dash-styled"
import {
    getTutorialCorrectAnswerOptionDisplayText,
    tutorialCorrectAnswers
} from "../../../../../../../../Models/API/QualifierModel"
import { turbine } from "neql"
import { useNumberInput } from "../../../../../../../../Hooks/useNumberInput"
import { MetricChooser } from "../MetricChooser"
import { TrainingTutorialVariables } from "../../../../../../../../Hooks/useQualifierTutorialCreate"
import { useDispatch } from "react-redux"
import { dirtinessChanged } from "../../../../../../../../Actions/Client/InputRender/InputRenderActions"

export interface TutorialCardSettingsProps {
    appID: string
    qualifier: turbine.TrainingTutorial
    showSettings: boolean
    isPreparingCreation?: boolean
    createOrUpdateTutorial: (
        params: TrainingTutorialVariables
    ) => Promise<boolean>
    isLoading: boolean
}

export const TutorialCardSettings: FC<TutorialCardSettingsProps> = ({
    appID,
    qualifier,
    showSettings,
    isPreparingCreation,
    createOrUpdateTutorial,
    isLoading
}) => {
    const dispatch = useDispatch()
    const cutoffValueInput = useNumberInput(
        qualifier.cutoffValue?.valueOf(),
        true
    )
    const [selectedCorrectAnswer, setSelectedCorrectAnswer] = useState(
        qualifier.correctAnswer
    )

    const [cutoffOperator, setCuttoffOperator] = useState(
        qualifier.cutoffOperator
    )

    const [selectedMetric, setSelectedMetric] = useState<
        string | null | undefined
    >(qualifier.cutoffMetricName)

    const formPrestine = useMemo(
        () =>
            isPreparingCreation !== true &&
            qualifier.cutoffOperator === cutoffOperator &&
            qualifier.cutoffValue === cutoffValueInput.value &&
            qualifier.correctAnswer === selectedCorrectAnswer,
        [
            isPreparingCreation,
            qualifier.cutoffOperator,
            qualifier.cutoffValue,
            qualifier.correctAnswer,
            cutoffOperator,
            cutoffValueInput.value,
            selectedCorrectAnswer
        ]
    )

    useEffect(() => {
        dispatch(dirtinessChanged(!formPrestine))
    }, [dispatch, formPrestine])

    const handleSave = useCallback(async () => {
        if (selectedMetric !== null && selectedMetric !== undefined) {
            await createOrUpdateTutorial({
                id: qualifier.id,
                cutoffOperator: cutoffOperator ?? undefined,
                tutorialCorrectAnswer: selectedCorrectAnswer ?? undefined,
                cutoffValue: cutoffValueInput.value,
                cutoffMetricName: selectedMetric,
                cutoffMetricDisplayName: selectedMetric
            })
        }
    }, [
        createOrUpdateTutorial,
        cutoffOperator,
        cutoffValueInput.value,
        qualifier.id,
        selectedCorrectAnswer,
        selectedMetric
    ])

    return (
        <div
            className={`flex flex-col lg:ml-7% space-y-3 mt-3 ${
                showSettings ? "" : "hidden"
            }`}
        >
            <div className="space-y-5">
                <MetricChooser
                    title="Answer is correct when"
                    appID={appID}
                    cutoffMetric={selectedMetric}
                    cutoffOperator={cutoffOperator}
                    onCutoffOperatorChange={setCuttoffOperator}
                    onCutoffValueChange={cutoffValueInput.onChange}
                    cutoffValue={cutoffValueInput.value?.toString()}
                    onCutoffMetricChange={setSelectedMetric}
                />

                <div className="flex items-center space-x-1">
                    <Text className="my-0 flex-shrink-0" size="sm">
                        Show correct answer after submission
                    </Text>
                    <ExclusiveChoiceDropdown
                        className="w-68"
                        testId={`qualifiers-tutorial-correct-answer`}
                        size="sm"
                        choices={tutorialCorrectAnswers}
                        value={selectedCorrectAnswer ?? undefined}
                        onClick={setSelectedCorrectAnswer}
                        renderChoice={getTutorialCorrectAnswerOptionDisplayText}
                    />
                </div>
            </div>
            <>
                {isLoading ? (
                    <div>
                        <Loader size="xxxl" className="my-0.5 w-max-content" />
                    </div>
                ) : (
                    <Button
                        size="sm"
                        testId={`qualifiers-tutorial-save-button`}
                        className="px-4 mt-3 w-max-content"
                        disabled={isLoading || formPrestine || !selectedMetric}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                )}
            </>
        </div>
    )
}
